import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { BehaviorSignalService } from '@thiememeulenhoff/ng-api-gateway';
import { BehaviorSignalsResponse } from '@thiememeulenhoff/types';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

export const BehaviorSignalStore = signalStore(
  { providedIn: 'root' },
  withState({ behaviorSignals: null as BehaviorSignalsResponse | null }),
  withRequestStatus(),
  withMethods(
    (store, behaviorSignalService = inject(BehaviorSignalService)) => {
      // Only the latest request may patch state, so a slow response for the
      // previous question never overwrites the current one.
      let latestRequest = 0;

      return {
        async fetchBehaviorSignals(
          actor: string,
          arrangementCode: string,
          contentIdentifier: string,
        ): Promise<BehaviorSignalsResponse | null> {
          latestRequest += 1;
          const request = latestRequest;
          const isLatest = () => request === latestRequest;

          patchState(store, {
            behaviorSignals: null,
            requestStatus: 'loading',
          });

          try {
            const behaviorSignals = await firstValueFrom(
              behaviorSignalService.getBehaviorSignals(
                actor,
                arrangementCode,
                contentIdentifier,
              ),
            );

            if (isLatest()) {
              patchState(store, { behaviorSignals, requestStatus: 'success' });
            }

            return behaviorSignals;
          } catch (error) {
            // 404 means the student has no attempts yet: nothing to show, not an error.
            if (error instanceof HttpErrorResponse && error.status === 404) {
              if (isLatest()) {
                patchState(store, { requestStatus: 'success' });
              }

              return null;
            }

            if (isLatest()) {
              patchState(store, {
                requestStatus: toRequestError(
                  error,
                  'Failed to fetch behavior signals',
                ),
              });
            }

            throw error;
          }
        },
      };
    },
  ),
);
